import { useEffect } from 'react'

/* The two global keyboard shortcuts (UX-DR-23).
 *
 * `/` focuses global search and `Ctrl/Cmd+S` saves wherever a record is being
 * edited. `Esc` is deliberately NOT here: each overlay binds it while it is
 * open, so the innermost one closes rather than all of them at once — which is
 * what a single global handler would do.
 *
 * The typing guard is the whole subtlety. A `/` handler that fires while
 * somebody is typing a route into a text field steals the character and moves
 * focus, and the user has no idea why. So the shortcut is ignored whenever the
 * event originates from somewhere text is being entered.
 */

/** True when the event came from a field where a character is being typed. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false

  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true

  // A rich-text or contenteditable region is typing too.
  return target.isContentEditable
}

export interface Shortcuts {
  /** `/` — focus global search. Ignored while typing.
   *
   *  Also bound to Ctrl/Cmd+K, which is the combination people arrive already
   *  knowing and the one the field advertises. Unlike `/` it fires from INSIDE
   *  a text field too: it cannot be mistaken for a character, so the typing
   *  guard that `/` needs would only make it fail where it is most useful. */
  onSearch?: () => void
  /**
   * `Ctrl/Cmd+S` — save the record being edited.
   *
   * Fires even from inside a field, because that is precisely where somebody
   * is when they reach for it, and it always preventDefaults: the browser's
   * own "save page" dialog appearing over a half-filled cargo checklist is a
   * worse outcome than any shortcut collision.
   */
  onSave?: () => void
}

/**
 * Binds the global shortcuts for as long as the component is mounted.
 *
 * Handlers are read through a ref-free closure that re-binds when they change,
 * which is correct here: these are mounted once in the shell and the identity
 * of the callbacks is stable.
 */
export function useShortcuts({ onSearch, onSave }: Shortcuts) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // Cmd on macOS, Ctrl elsewhere. Both, rather than detecting the platform:
      // an external keyboard on a Mac may well send Ctrl.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        if (!onSave) return
        event.preventDefault()
        onSave()
        return
      }

      // Cmd/Ctrl+K, the combination the field advertises. Always preventDefault:
      // in Firefox this is the browser's own search bar, and the page's search
      // losing to the chrome's is exactly the collision users complain about.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        if (!onSearch) return
        event.preventDefault()
        onSearch()
        return
      }

      if (event.key === '/' && onSearch) {
        // The guard: `/` is a character before it is a shortcut.
        if (isTypingTarget(event.target)) return
        // Modifiers mean something else entirely — Cmd+/ is a browser shortcut.
        if (event.metaKey || event.ctrlKey || event.altKey) return

        event.preventDefault()
        onSearch()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onSearch, onSave])
}
